import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from "@angular/router";
import { Events } from '../../helpers/events';

// ionic
import { AlertController, PopoverController } from '@ionic/angular';

// other libraries
import * as moment from "moment";

// rxjs
import { Subject } from "rxjs";
import { debounceTime, take, takeUntil } from 'rxjs/operators';

// components
import { AttendeeDetailComponent } from "../../components/attendee-detail/attendee-detail.component";
import { AppointmentAttendeesComponent } from "../../components/appointment-attendees/appointment-attendees.component";
import { AppointmentAddParticipantComponent } from "../../components/appointment-add-participant/appointment-add-participant.component";

// services
import { AppointmentService } from "../../services/appointment.service";
import { ConversationService } from "../../services/conversation.service";

// models
import { Event } from "../../models/event";
import { User } from "../../models/user";
import { Appointment } from "../../models/appointment";
import { Conversation } from "../../models/conversation";
import { Participant } from "../../models/participant";
import { ParticipantTimeslot } from "../../models/participant-timeslot";

// config
import { environment } from "../../../environments/environment";

// extends
import { BasePage } from "../base";

@Component({
    selector: 'app-appointments',
    templateUrl: './appointments.page.html',
    styleUrls: ['./appointments.page.scss'],
})

export class AppointmentsPage extends BasePage implements OnInit, OnDestroy {
    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * user
     *
     * @type {User}
     */
    public user: User = new User;

    /**
     * active event for user
     *
     * @type Event
     */
    public event: Event = new Event;

    /**
     * selected language
     *
     * @type string
     */
    public userLang: string;

    /**
     * user token for appointment download
     */
    public userToken: string;

    /**
     * initial loading state
     *
     * @type {boolean}
     */
    public loading: boolean = true;

    /**
     *
     * @type {boolean}
     */
    public showAllButton: boolean = false;

    /**
     *
     * @type {boolean}
     */
    public selectedSingle: boolean = false;

    /**
     *
     * @type {boolean}
     */
    public upcomingAppointments: boolean = false;

    /**
     * Participant timeslots
     *
     * @type {ParticipantTimeslot}
     */
    public participantTimeslot: ParticipantTimeslot = new ParticipantTimeslot;

    /**
     * invalid appointments due to deleted participants
     *
     * @type number
     */
    public deletedAppointmentsCount: number;

    /**
     * status to filter
     *
     * @type string
     */
    public filterStatusValue: string = 'all';

    /**
     * which select type should be used.. alert/popover/action-sheet
     *
     * @type {string}
     */
    public selectType: string = 'popover';

    /**
     * @type Appointment[]
     */
    public unprocessedAppointments: Appointment[] = [];
    public processedAppointments: Appointment[] = [];
    public allAppointments: Appointment[] = [];
    public pastAppointments: Appointment[] = [];
    public pastAppointmentsCounter: number = 5;

    public scheduledAppointmentCount: number;

    /**
     * Text with reason which will be sended to other participant
     *
     * @type {string}
     */
    public cancelReason: string = '';

    public searchTerm: string = "";

    public personalAppointmentStartsAt: string = "";

    public order: string = "starts_at:asc";

    /**
     * api
     *
     * @type {string}
     */
    public api = environment.api;

    // handlers for events
    eventChangeHandler;
    refreshHandler;

    /**
     * filtered detail of appointment
     *
     * @type {number}
     */
    public selectedAppointmentId: number;

    /**
     * constructor
     *
     * @param appointmentService
     * @param conversationService
     * @param alertCtrl
     * @param appEvents
     * @param popoverController
     */
    constructor(
        public route: ActivatedRoute,
        public appointmentService: AppointmentService,
        public conversationService: ConversationService,
        public alertCtrl: AlertController,
        public appEvents: Events,
        public popoverController: PopoverController
    ) {
        super();
        // user language
        this.userLang = this.translate.currentLang;

        this.userToken = localStorage.getItem('token');
    }

    /**
     * on init
     *
     * @return void
     */
    ngOnInit() {
        if (this.route.snapshot.params['id']) {
            this.selectedAppointmentId = this.route.snapshot.params['id'];
        }

        if (this.route.snapshot.params['starts_at']) {
            this.personalAppointmentStartsAt = this.route.snapshot.params['starts_at'];
        }

        this.eventChangeHandler = () => {
            // to avoid issue when showing appointments from previous event
            this.loading = true;
            this.attachEvents();
            // reset search and filter
            this.searchTerm = "";
            this.filterStatusValue = "all";
        }

        this.refreshHandler = () => {
            // check current user, as this handler can be called from tabs
            this.userService.getCurrentUser()
                .pipe(
                    take(1)
                ).subscribe((user) => {
                    if (user.id) {
                        this.user = user;
                        this.refresh();
                    }
                });
        }

        this.userService.getCurrentUser()
            .pipe(
                takeUntil(this.ngUnsubscribe),
                debounceTime(0)
            ).subscribe(
                (user) => {
                    if (user.id) {
                        this.user = user;
                        // this.refresh();
                    }
                });

        // attach hooks on event change
        this.appEvents.subscribe('event:changed', this.eventChangeHandler);

        this.attachEvents();
    }

    ionViewWillEnter() {
        // console.info('view will enter appointments');
        this.userService.getCurrentUser()
            .pipe(
                take(1),
                debounceTime(0)
            ).subscribe(
                (user) => {
                    if (user.id) {
                        this.user = user;
                        this.refresh();
                    }
                });
    }

    attachEvents() {
        this.appEvents.unsubscribe('refresh:appointments');
        this.appEvents.subscribe('refresh:appointments', this.refreshHandler);
    }

    /**
     * refresh dashboard
     *
     * @return void
     */
    public refresh($event?) {
        if (this && this.user && this.user.id && this.user.selected_participant) {
            // TODO[jg] remove this and replace it with getCurrentEvent !!
            let subscription = this.eventService.getById(this.user.selected_participant.event_id).subscribe((event: Event) => {

                this.event = new Event(event, this.user);
                this.allAppointments = [];
                this.showAllButton = false;
                this.selectedSingle = false;
                this.upcomingAppointments = false;

                // get appointments
                let subscriptionAppointments = this.appointmentService.getAllByParticipant(this.user.selected_participant).subscribe((appointments: Appointment[]) => {
                    this.allAppointments = [];

                    // assign appointment models to array
                    appointments.forEach(appointment => {
                        let appointmentObj = new Appointment(appointment);
                        appointmentObj.setParticipantStatus(this.user.selected_participant_id);
                        this.allAppointments.push(appointmentObj);
                    });

                    // filter appointments where there are both participants
                    this.allAppointments = this.allAppointments.filter((appointment: Appointment) => {
                        return appointment.participants && appointment.participants.length > 1;
                    });

                    if (this.selectedAppointmentId) {
                        // count if there is something to show in upcoming section
                        this.upcomingAppointments = this.allAppointments.filter((item: Appointment) => {
                            return !item.past && (item.participantStatus != 0 || (item.invitation_message && item.invitation_message.participant_id == this.user.selected_participant_id))
                        }).length > 0;
                        // show only selected appointments
                        this.allAppointments = this.allAppointments.filter((appointment: Appointment) => {
                            return appointment.id == this.route.snapshot.params['id'];
                        });
                        this.showAllButton = true;
                        this.selectedSingle = true;
                        this.loading = false;
                        this.setPastAppointments();
                        this.applyFilter();
                        this.setUnprocessedAppointments();
                        this.setSchedulledCount();
                    } else if (this.user.selected_participant.event.use_timeslots) {
                        let subscriptionTimeslots = this.participantService.getTimeslot(this.user.selected_participant.id).subscribe((data) => {
                            if (data['participanTimeslots'] && data['participanTimeslots'].hasOwnProperty('id')) {
                                // insert my blocked data into appointment list
                                this.participantTimeslot = new ParticipantTimeslot(data['participanTimeslots']);
                                this.participantTimeslot.markAppointment(this.allAppointments);

                                let privateAppointments = this.participantTimeslot.generatePrivateAppointment();
                                privateAppointments.forEach((privateAppointment) => {
                                    this.allAppointments.push(new Appointment(privateAppointment));
                                });
                            }

                            if (this.personalAppointmentStartsAt) {
                                this.allAppointments = this.allAppointments.filter((appointment) => {
                                    return appointment.starts_at == this.personalAppointmentStartsAt;
                                });

                                this.showAllButton = true;
                                this.selectedSingle = true;
                            } else {
                                this.deletedAppointmentsCount = appointments.length - this.allAppointments.length;
                            }

                            this.setPastAppointments();
                            this.applyFilter();
                            this.orderBy(this.order);

                            // set unprocessedAppointments
                            this.setUnprocessedAppointments();
                            this.setSchedulledCount();
                        });
                        this.plt.activeSubscriptions.push(subscriptionTimeslots);
                    } else {
                        this.deletedAppointmentsCount = appointments.length - this.allAppointments.length;

                        this.setPastAppointments();
                        this.applyFilter();
                        this.orderBy(this.order);

                        // set unprocessedAppointments
                        this.setUnprocessedAppointments();
                        this.setSchedulledCount();
                    }

                    // hide loading
                    if ($event) {
                        $event.target.complete();
                    } else {
                        this.loading = false;
                    }
                });
                this.plt.activeSubscriptions.push(subscriptionAppointments);
            },
                (error) => {
                    // TODO
                    this.showError(error);
                });
            this.plt.activeSubscriptions.push(subscription);
        }
    }

    public showAll() {
        this.personalAppointmentStartsAt = null;
        this.selectedAppointmentId = null;
        this.routerExtService.softNavigate(this.plt.defaultLink + '/appointments');
        this.refresh();
    }

    /**
     * filter processed appointments
     */
    public applyFilter() {
        switch (this.filterStatusValue) {
            case 'confirmed': {
                this.processedAppointments = this.allAppointments.filter((item) => { return (!item.past && (item.participantStatus == 1)) });
                break;
            }
            case 'personal': {
                this.processedAppointments = this.allAppointments.filter((item) => { return (!item.past && (item.status == 4)) });
                break;
            }
            case 'canceled': {
                this.processedAppointments = this.allAppointments.filter((item) => { return (!item.past && (item.participantStatus == 2 || item.participantStatus == 3)) });
                break;
            }
            case 'pending': {
                this.processedAppointments = this.allAppointments.filter((item) => { return !item.past && (item.participantStatus == 0 && item.invitation_message && item.invitation_message.participant_id == this.user.selected_participant_id) });
                break;
            }
            default: {
                this.processedAppointments = this.allAppointments.filter((item) => { return !item.past && (item.participantStatus != 0 || (item.invitation_message && item.invitation_message.participant_id == this.user.selected_participant_id)) });
                break;
            }
        }

        // process searchbar
        if (this.searchTerm.length) {
            var searchValue = this.searchTerm.toLowerCase();

            this.processedAppointments = this.processedAppointments.filter((item: Appointment) => {
                let found = false;
                // search by participants name
                if (item.participants && item.participants.length) {
                    item.participants.forEach((participant: Participant) => {
                        if (participant.id != this.user.selected_participant_id && participant.getFullName().toLowerCase().indexOf(searchValue) > -1) {
                            found = true;
                        }
                    });
                }

                if (found) {
                    return true;
                }

                // search by location
                if (item.location && item.location.toLowerCase().indexOf(searchValue) > -1) {
                    return true;
                }

                // search by note
                if (item.description && item.description.toLowerCase().indexOf(searchValue) > -1) {
                    return true;
                }

                return false;
            });
        }

        this.loading = false;
    }

    public async openCancelPersonalAppointment(appointment: Appointment) {

        var options = {
            cssClass: 'confirm',
            header: this.translate.instant('APPOINTMENTS_MODAL_CANCEL_TITLE'),
            message: this.translate.instant('APPOINTMENTS_MODAL_CANCEL_TEXT'),
            buttons: [{
                text: this.translate.instant('BUTTON_CANCEL'),
                role: 'cancel',
                cssClass: 'secondary',
                handler: () => { }
            }, {
                text: this.translate.instant('BUTTON_OK'),
                role: '',
                cssClass: 'primary',
                handler: () => { this.cancelPersonalAppointment(appointment); }
            }]
        };

        let alert = await this.alertCtrl.create(options);
        await alert.present();
    }

    public cancelPersonalAppointment(appointment: Appointment) {
        // mark slots as free where they were blocked in time of appointment
        this.participantTimeslot.markAppointment([appointment], 'free');

        // mark proper timeslots as free and save this setting
        let data = JSON.parse(JSON.stringify(this.participantTimeslot));
        // finalize data format, remove unused items
        Object.keys(this.participantTimeslot.timeslots['items']).forEach((key) => {
            this.participantTimeslot.timeslots['items'][key].forEach((slot, index) => {
                delete data.timeslots['items'][key][index].appointment;
            })
        });

        // store results in database
        this.participantService.updateTimeslot(this.user.selected_participant.id, data).subscribe((data) => {
            this.overlayService.showSuccess(data.message);
            this.loading = false;
        }, error => {
            this.overlayService.showError(error.error.message);
            this.loading = false;
        });

        this.processedAppointments = this.processedAppointments.filter(appointmentItem => {
            return appointmentItem.starts_at !== appointment.starts_at;
        })
    }

    public async openAnswerAppointment(appointment: Appointment, status: number) {

        var secondParticipant;

        if (appointment.participants?.length > 2 && appointment.invitation_message?.participant_id != this.user.selected_participant_id) {
            secondParticipant = appointment.participants.find((item: Participant) => {
                return item.id == appointment.invitation_message?.participant_id;
            });
        }
        else {
            secondParticipant = appointment.participants.filter((item: Participant) => {
                return item.id != this.user.selected_participant_id;
            })[0];
        }


        var options = {};

        switch (status) {
            case 2: {
                options = {
                    cssClass: 'confirm',
                    header: this.translate.instant('APPOINTMENTS_MODAL_DECLINE_TITLE'),
                    message: this.translate.instant('APPOINTMENTS_MODAL_DECLINE_TEXT'),
                    inputs: [
                        {
                            name: 'reason',
                            type: 'text',
                            placeholder: this.translate.instant('APPOINTMENTS_MODAL_DECLINE_REASON_PLACEHOLDER', {
                                name: secondParticipant.getFullName()
                            })
                        }
                    ],
                    buttons: [{
                        text: this.translate.instant('BUTTON_CANCEL'),
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: () => { }
                    }, {
                        text: this.translate.instant('BUTTON_OK'),
                        role: '',
                        cssClass: 'primary',
                        handler: (data) => {
                            this.addCancelReason(data.reason);
                            this.answerAppointment(appointment, status);
                        }
                    }]
                };

                break;
            }
            case 3: {
                options = {
                    cssClass: 'confirm',
                    header: this.translate.instant('APPOINTMENTS_MODAL_CLOSE_TITLE'),
                    message: this.translate.instant('APPOINTMENTS_MODAL_CLOSE_TEXT'),
                    inputs: [
                        {
                            name: 'reason',
                            type: 'text',
                            placeholder:
                                (appointment.participants?.length > 2 && appointment.invitation_message?.participant_id == this.user.selected_participant_id)
                                    ? this.translate.instant('APPOINTMENTS_MULTIPLE_ATTENDEES_MODAL_DECLINE_REASON_PLACEHOLDER')
                                    : this.translate.instant('APPOINTMENTS_MODAL_DECLINE_REASON_PLACEHOLDER', {
                                        name: secondParticipant.getFullName()
                                    })
                        }
                    ],
                    buttons: [{
                        text: this.translate.instant('BUTTON_CANCEL'),
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: () => { }
                    }, {
                        text: this.translate.instant('BUTTON_OK'),
                        role: '',
                        cssClass: 'primary',
                        handler: (data) => {
                            this.addCancelReason(data.reason);
                            this.answerAppointment(appointment, status);
                        }
                    }]
                };

                break;
            }
            default: {
                options = {
                    cssClass: 'confirm',
                    header: this.translate.instant('APPOINTMENTS_MODAL_ACCEPT_TITLE'),
                    message: this.translate.instant('APPOINTMENTS_MODAL_ACCEPT_TEXT'),
                    buttons: [{
                        text: this.translate.instant('BUTTON_CANCEL'),
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: () => { }
                    }, {
                        text: this.translate.instant('BUTTON_OK'),
                        role: '',
                        cssClass: 'primary',
                        handler: () => { this.answerAppointment(appointment, status); }
                    }]
                };

                break;
            }
        }

        let alert = await this.alertCtrl.create(options);
        await alert.present();
    }

    /**
     * answer appointment
     *
     * @param appointment
     * @param status
     *
     * @return void
     */
    public answerAppointment(appointment: Appointment, status: number) {
        this.loading = true;
        this.showLoading();

        const oldStatus = appointment.status;
        appointment.status = status;
        appointment.participantStatus = status;

        if (appointment.status === 3 || appointment.status === 2) {
            appointment.reason = this.cancelReason;
        }

        this.conversationService.answerAppointment(appointment.invitation_message.conversation_id, appointment)
            .subscribe((conversation: Conversation) => {
                if (appointment.status == 3) {
                    this.overlayService.showSuccess(this.translate.instant('APPOINTMENTS_CANCELED_TEXT'));
                }
                if (appointment.status == 2) {
                    this.overlayService.showSuccess(this.translate.instant('APPOINTMENTS_DECLINED_TEXT'));
                    this.plt.readAppointmentNotification(1);
                }
                if (appointment.status == 1) {
                    this.overlayService.showSuccess(this.translate.instant('APPOINTMENTS_ACCEPTED_TEXT'));
                    this.plt.readAppointmentNotification(1);
                }
                this.cancelReason = '';
                appointment.status = oldStatus;
                this.applyFilter();

                // set unprocessedAppointments
                this.setUnprocessedAppointments();
                this.setSchedulledCount();

                this.loading = false;
            }, error => {
                this.overlayService.showError(error.error.message);
                appointment.status = oldStatus;
                appointment.participantStatus = oldStatus;
                this.cancelReason = '';
                this.loading = false;
            });
    }

    public addCancelReason(reason) {
        this.cancelReason = reason;
    }

    /**
     * check if i can see profile details
     */
    public canProfileDetails(participant: Participant) {
        return (
            this.user.selected_participant.event.checkGroupPermition(participant.group_id, 'visible') &&
            this.user.selected_participant.event.checkGroupPermition(participant.group_id, 'profile_details')
        );
    }

    /**
     * check if i can see participants picture
     */
    public canPicture(participant: Participant) {
        return this.user.selected_participant && this.user.selected_participant.event.checkGroupPermition(participant.group_id, 'profile_picture');
    }

    /**
    * Order by name
    */
    public orderBy(sort) {
        this.order = sort;
        var orderParts = sort.split(":");
        var orderBy = orderParts[0];
        var orderDirection = orderParts[1] == 'desc' ? -1 : 1;
        var self = this;

        this.processedAppointments = this.processedAppointments.sort(function (a, b) {
            var item1 = "";
            var item2 = "";

            switch (orderBy) {
                case 'name': {

                    if (a.participantStatus != 4) {
                        var secondParticipantA = a.participants.filter((item: Participant) => {
                            return item.id != self.user.selected_participant_id;
                        })[0];
                        item1 = secondParticipantA.getFullName();
                    } else {
                        item1 = "zzz"; // group personal appointments to end/start of list
                    }


                    if (b.participantStatus != 4) {
                        var secondParticipantB = b.participants.filter((item: Participant) => {
                            return item.id != self.user.selected_participant_id;
                        })[0];
                        item2 = secondParticipantB.getFullName();
                    } else {
                        item2 = "zzz"; // group personal appointments to end/start of list
                    }

                    break;
                }
                case 'starts_at': {
                    item1 = a.starts_at;
                    item2 = b.starts_at;
                    break;
                }
                default: break;
            }

            if (item1 < item2) {
                return -1 * orderDirection;
            }
            if (item1 > item2) {
                return 1 * orderDirection;
            }
            return 0;
        });
    }

    /**
     * download appointment
     *
     * @return void
     */
    public download(appointment: Appointment) {
        if (this.plt.is('cordova')) {
            this.plt.addToCalendar(appointment, this.user.selected_participant_id);
        } else {
            window.location.href = this.api + '/appointment/download/' + appointment.id + '?token=' + this.userToken;
        }
    }

    /**
     * generate appointment
     *
     * @return void
     */
    public generate(appointment: Appointment) {
        if (this.plt.is('cordova')) {
            this.plt.addToCalendar(appointment, this.user.selected_participant_id);
        } else {
            window.location.href = this.api + '/appointment/generate?token=' + this.userToken + this.generateAppointment(appointment);
        }
    }

    public generateAppointment(appointment: Appointment) {
        let linkParams = '';
        let meeting = {
            description: this.translate.instant('APPOINTMENTS_PERSONAL_APPOINTMENT'),
            start: moment(appointment.date_starts_at).utc().format('YYYYMMDDTHHmm'),
            end: moment(appointment.date_ends_at).utc().format('YYYYMMDDTHHmm'),
            location: '',
            note: appointment.description ? appointment.description : ''
        }
        Object.keys(meeting).forEach(key => {
            linkParams += '&' + key + '=' + meeting[key]
        });

        return linkParams;
    }

    public setUnprocessedAppointments() {
        this.unprocessedAppointments = this.allAppointments.filter((item) => {
            return !item.past
                && (
                    item.participantStatus == 0
                    && ( // if no invitation message, user was added to appointment with multiple users
                        (!item.invitation_message ||
                            (item.invitation_message && item.invitation_message.participant_id != this.user.selected_participant_id)
                        )
                    )
                )
        });
    }

    public setPastAppointments() {
        this.pastAppointments = this.allAppointments.filter((item) => { return item.past });
        this.pastAppointments = this.pastAppointments.sort(function (a, b) {
            if (a.starts_at < b.starts_at) {
                return 1;
            }
            if (a.starts_at > b.starts_at) {
                return -1;
            }
            return 0;
        });
    }

    public setSchedulledCount() {
        this.scheduledAppointmentCount = this.allAppointments.filter((item) => { return (item.participantStatus == 1 || item.participantStatus == 4) && !item.past }).length;
    }

    public loadMorePast() {
        this.pastAppointmentsCounter += 5;
    }

    public filterUsed() {
        return this.filterStatusValue != 'all' || this.searchTerm.length;
    }

    public resetFilter() {
        this.filterStatusValue = 'all';
        this.searchTerm = '';

        if (this.selectedSingle) {
            this.routerExtService.softNavigate(this.plt.defaultLink + '/appointments');
            this.refresh();
        } else {
            this.applyFilter();
        }
    }

    /**
     * go to participant detail page
     *
     * @param participant
     *
     * @return void
     */
    public async showParticipantDetail(participant: Participant) {

        this.routerExtService.softNavigate();

        const modal = await this.modalController.create({
            component: AttendeeDetailComponent,
            cssClass: 'attendee-modal',
            componentProps: {
                attendee: participant
            }
        });

        modal.onWillDismiss().then((data) => {
            if (data.data?.action == 'close' || data.role == 'backdrop') {
                this.routerExtService.softBack();
            }
        });

        return await modal.present();
    }

    /**
     * open video call
     *
     */
    public openCall(appointment) {
        let attendee = appointment.participants.filter(p => p.id != this.user.selected_participant_id)[0];
        let link = appointment.chat_group ? this.plt.defaultLink + '/messages/channel/' + appointment.chat_group.id : this.plt.defaultLink + '/messages/direct/' + attendee.id;

        this.router.navigate([link], {
            // replaceUrl: true,
            queryParams: {
                call: true
            }
        });
    }

    /**
     * show appointment participant list
     * @return void
     */
    async openAppointmentParticipants(ev: any, appointment: Appointment) {
        const popover = await this.popoverController.create({
            component: AppointmentAttendeesComponent,
            event: ev,
            componentProps: {
                appointment: appointment
            },
            cssClass: 'appointment-participant-list',
            id: 'appointment-participant-list'
        });

        popover.onWillDismiss().then((data) => { });

        await popover.present();
    }

    /**
     * go to participant detail page
     *
     * @param appointment
     *
     * @return void
     */
    public async addParticipants(appointment: Appointment) {

        this.routerExtService.softNavigate();

        const modal = await this.modalController.create({
            component: AppointmentAddParticipantComponent,
            cssClass: 'attendee-modal',
            componentProps: {
                appointment: { ...appointment }
            }
        });

        modal.onWillDismiss().then((data) => {
            if (data.data?.action == 'close' || data.role == 'backdrop') {
                this.routerExtService.softBack();
            }

            if (data.data?.appointment) {
                // this.refresh();
                this.processedAppointments.forEach((a, i) => {
                    if (a.id == data.data.appointment.id) {
                        this.processedAppointments[i] = { ...data.data.appointment };
                    }
                });

            }
        });

        return await modal.present();
    }

    /**
     * on destroy
     *
     * @return void
     */
    ngOnDestroy() {
        if (this.eventChangeHandler) {
            this.appEvents.unsubscribe('event:changed', this.eventChangeHandler);
        }
        if (this.refreshHandler) {
            this.appEvents.unsubscribe('refresh:appointments', this.refreshHandler);
        }

        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
